import { accomTypes, accommodationSubItemTypes } from '../BookingFieldset/helpers';

/**
 * Derives the number of instances (radio-button slots) for a given sub-item key
 * from selectedOptions. Camping tents and hotel rooms both store counts via
 * rooms: [{ roomKey, roomCount }] on their respective accommodation entry.
 */
function getInstanceCount(subItemKey, accommodationType, selectedOptions) {
  const { accommodations = [], stopovers = [] } = selectedOptions;

  if (accommodationType === accommodationSubItemTypes.CAMPSITE_TENT) {
    const campingAccom = accommodations.find((a) => a.accommodationType === accomTypes.CAMPING);
    return campingAccom?.rooms?.find((r) => r.roomKey === subItemKey)?.roomCount ?? 0;
  }

  // Hotel — primary accommodation or stopover hotel
  const hotelAccom = accommodations.find((a) => a.accommodationType === accomTypes.HOTEL);
  if (hotelAccom) {
    return hotelAccom.rooms?.find((r) => r.roomKey === subItemKey)?.roomCount ?? 0;
  }

  // Stopover hotel fallback (matches existing index.jsx logic)
  if (stopovers.length > 0) {
    const stopoverHotel = stopovers[0]?.hotel;
    return stopoverHotel?.rooms?.find((r) => r.roomKey === subItemKey)?.roomCount ?? 0;
  }

  return 0;
}

/**
 * Converts the unified accommodationSubItems array from the API response into
 * the rooms view-model consumed by RoomAllocation.jsx and the rest of
 * BookingDetails. Each entry gets:
 *   key        — sub-item GUID
 *   code       — hotel room type code (e.g. 'DB') or '' for tents
 *   roomName   — display name
 *   capacity   — numberOfBerths for tents (used by RoomAllocation isFull logic)
 *   isSelected — true when at least one instance was booked
 *   isActive   — alias for isSelected (kept for backwards compat)
 *   items      — [{ occupancy: 0 }, ...] one slot per booked instance
 *   subItem    — the raw sub-item (for camping validation to read paxOccupancyRuleSets)
 */
export function buildRoomsViewModel(accommodationSubItems, selectedOptions) {
  if (!accommodationSubItems?.length) return undefined;

  return accommodationSubItems.map((subItem) => {
    const isTent = subItem.accommodationType === accommodationSubItemTypes.CAMPSITE_TENT;
    const instanceCount = getInstanceCount(subItem.key, subItem.accommodationType, selectedOptions);

    return {
      key: subItem.key,
      code: isTent ? '' : subItem.code ?? '',
      roomName: subItem.title,
      capacity: isTent ? subItem.numberOfBerths : undefined,
      isSelected: instanceCount > 0,
      items: Array.from({ length: instanceCount }, () => ({ occupancy: 0 })),
      subItem,
    };
  });
}

/**
 * Returns true when any sub-item is a camping tent.
 */
export function hasCampsiteTents(accommodationSubItems) {
  return (
    accommodationSubItems?.some(
      (s) => s.accommodationType === accommodationSubItemTypes.CAMPSITE_TENT
    ) ?? false
  );
}
